import { useMemo, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { MiniGrid } from "./MiniGrid";
import { Modal } from "./Modal";
import { ErrorState, Money, MktButton, StatePill, Stat, ZoneBadge } from "./primitives";
import { fmtMicro, shortAddr, slotCount, symbolFor, bboxFromCells, isPartialListing, displayParcelId, type ZoneKey } from "./mkt-format";
import { mktError } from "./mkt-errors";
import { useListing, useMarketplaceConfig, useListingZone, useAssetOffers, listingCells } from "./useMarketplace";
import { useSession } from "./useSession";
import { goMarketplace, viewOnGrid, type MarketplaceRouteState } from "./mkt-routing";
import { landAssetKey, marketplaceApi, MarketplaceError, type Listing, type MarketplaceConfig, type PermitResponse } from "../../lib/marketplace-api";
import { useBuySettlement, buildFillOrder, buildFillPermit } from "./buy-settlement";
import { LandProvenanceTimeline } from "./LandProvenanceTimeline";
import { GenesisBadge } from "../genesis/GenesisBadge";
import { FoundingProvenance } from "../genesis/FoundingProvenance";
import { useGenesisParcel } from "../genesis/useGenesis";
import { useT } from "../../i18n";

type TFn = (path: string, vars?: Record<string, string | number>) => string;

const ZONE_FILL: Record<ZoneKey, string> = {
  standard: "fill-zone-standard", premium: "fill-zone-premium", vip: "fill-zone-vip",
  reserve: "fill-zone-reserve", grid_zero: "fill-neon-coral", unknown: "fill-neon-teal",
};

const OFFER_DURATIONS: { labelKey: string; seconds: number }[] = [
  { labelKey: "marketplace.detail.dur1h", seconds: 3600 },
  { labelKey: "marketplace.detail.dur24h", seconds: 24 * 3600 },
  { labelKey: "marketplace.detail.dur3d", seconds: 3 * 24 * 3600 },
  { labelKey: "marketplace.detail.dur7d", seconds: 7 * 24 * 3600 },
];

function errText(t: TFn, e: unknown): { title: string; hint: string; signIn?: boolean } {
  if (e instanceof MarketplaceError) return mktError(t, e.code, e.message);
  return mktError(t, undefined, e instanceof Error ? e.message : undefined);
}

export function LandDetail({ orderHash, route }: { orderHash: string; route: MarketplaceRouteState }) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const listingQuery = useListing(orderHash);
  const listing = listingQuery.data ?? null;
  const cells = useMemo(() => (listing ? listingCells(listing) : []), [listing]);
  const zone = useListingZone(cells.length ? cells : undefined);
  const session = useSession();
  const assetKey = listing ? landAssetKey(listing.chain_id, listing.collection, listing.token_id) : null;
  const offersQuery = useAssetOffers(assetKey);
  const { member: genesis } = useGenesisParcel(listing ? displayParcelId(listing) : null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  if (listingQuery.isError) {
    return (
      <div className="max-w-md mx-auto py-12">
        <ErrorState title={t("marketplace.detail.listingNotFound")} detail={t("marketplace.detail.listingNotFoundDetail")} testId="land-detail-error"
          onRetry={() => goMarketplace("land", route.query)} />
      </div>
    );
  }
  if (!listing) {
    return <div data-testid="land-detail-loading" className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">{t("marketplace.detail.loadingParcel")}</div>;
  }

  const sym = symbolFor(listing.currency, config);
  const isSeller = !!session.wallet && session.wallet === listing.seller.toLowerCase();
  const isActive = listing.status === "active";
  const bbox = cells.length ? bboxFromCells(cells) : null;
  const offers = offersQuery.data?.offers ?? [];

  return (
    <div data-testid="land-detail" data-order-hash={orderHash} className="max-w-5xl mx-auto">
      <button
        type="button"
        data-testid="land-detail-back"
        onClick={() => goMarketplace("land", route.query)}
        className="inline-flex items-center gap-2 mb-6 font-mono text-[10.5px] tracking-[0.16em] uppercase text-text-secondary hover:text-neon-teal transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden><path d="M9 6H3m0 0l3-3M3 6l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        {t("marketplace.detail.backToLand")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="neon-glass rounded-xl border border-border-divider overflow-hidden">
          <div className="relative aspect-square bg-elevated/40">
            <MiniGrid cells={cells} zoneClass={ZONE_FILL[zone]} testId="land-detail-minimap" />
            <div className="absolute top-3 left-3"><ZoneBadge zone={zone} testId="land-detail-zone" /></div>
            <div className="absolute top-3 right-3"><StatePill state={listing.status} testId="land-detail-state" /></div>
          </div>
        </div>

        <div className="flex flex-col">
          <h1 className="font-display text-3xl font-semibold text-text-primary tracking-tight flex items-center gap-3 flex-wrap">
            {t("marketplace.common.parcelNumber", { id: displayParcelId(listing) })}
            {genesis?.is_genesis && <GenesisBadge member={genesis} testId="land-detail-genesis-badge" />}
            {isPartialListing(listing) && (
              <span data-testid="land-detail-partial-badge" className="font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded border border-neon-coral/50 text-neon-coral">{t("marketplace.common.partialTag")}</span>
            )}
          </h1>
          <p className="mt-1 font-mono text-[11px] tracking-[0.14em] text-text-muted">{t("marketplace.detail.seller", { addr: shortAddr(listing.seller) })}</p>

          <div className="mt-6 flex items-end gap-3">
            <Money amount={fmtMicro(listing.price_micro)} symbol={sym} className="text-3xl text-neon-teal" testId="land-detail-price" />
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 neon-glass rounded-lg border border-border-divider p-4">
            <Stat label={t("marketplace.detail.slots")} testId="land-detail-slots">{slotCount(cells)}</Stat>
            {bbox && <Stat label={t("marketplace.detail.footprint")}>{bbox.w}×{bbox.h}</Stat>}
            <Stat label={t("marketplace.detail.floor")}>{listing.floor_micro ? `${fmtMicro(listing.floor_micro)} ${sym}` : "—"}</Stat>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <MktButton
              variant="primary"
              testId="land-detail-buy-btn"
              disabled={!isActive || isSeller}
              onClick={() => setBuyOpen(true)}
            >
              {isSeller ? t("marketplace.detail.yourListing") : isActive ? t("marketplace.detail.buyNow") : t("marketplace.detail.unavailable")}
            </MktButton>
            {config?.offers_enabled && (
              <MktButton variant="ghost" testId="land-detail-offer-btn" disabled={isSeller} onClick={() => setOfferOpen(true)}>
                {t("marketplace.detail.makeOffer")}
              </MktButton>
            )}
            {bbox && (
              <MktButton
                variant="ghost"
                testId="land-detail-view-grid"
                onClick={() => viewOnGrid(bbox.x + bbox.w / 2, bbox.y + bbox.h / 2, Math.max(bbox.w, bbox.h) + 6)}
              >
                {t("marketplace.detail.viewOnGrid")}
              </MktButton>
            )}
          </div>

          {isSeller && (
            <p className="mt-3 font-mono text-[10.5px] text-text-muted">{t("marketplace.detail.sellerNote")}</p>
          )}
        </div>
      </div>

      {/* Offers */}
      <div className="mt-12" data-testid="land-detail-offers">
        <h2 className="font-display text-lg font-semibold text-text-primary uppercase tracking-[0.02em] mb-4">{t("marketplace.detail.openOffers")}</h2>
        {offers.length === 0 ? (
          <p className="font-mono text-[11px] text-text-muted">{t("marketplace.detail.noOffersParcel")}</p>
        ) : (
          <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
            {offers.map((o) => (
              <div key={o.offer_hash} data-testid="land-offer-row" className="flex items-center justify-between px-4 py-3">
                <span className="font-mono text-[11px] text-text-secondary">{shortAddr(o.offeror)}</span>
                <Money amount={fmtMicro(o.amount_micro)} symbol={symbolFor(o.currency, config)} className="text-base text-text-primary" />
              </div>
            ))}
          </div>
        )}
      </div>

      <LandProvenanceTimeline assetKey={assetKey} config={config} />

      <FoundingProvenance parcelId={displayParcelId(listing)} />

      {buyOpen && <BuyModal listing={listing} config={config} session={session} onClose={() => setBuyOpen(false)} />}
      {offerOpen && assetKey && (
        <OfferModal assetKey={assetKey} config={config} session={session}
          onClose={() => setOfferOpen(false)} onDone={() => offersQuery.refetch()} />
      )}
    </div>
  );
}

// ---- Buy flow: prepare-buy (reserve) → permit (authority signs) → wallet approve + fill ----
function BuyModal({
  listing, config, session, onClose,
}: {
  listing: Listing;
  config?: MarketplaceConfig | null;
  session: ReturnType<typeof useSession>;
  onClose: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const { address } = useAccount();
  const sym = symbolFor(listing.currency, config);
  const [phase, setPhase] = useState<"review" | "reserving" | "authorizing" | "ready">("review");
  const [error, setError] = useState<{ title: string; hint: string } | null>(null);
  const [permit, setPermit] = useState<PermitResponse | null>(null);
  const buy = useBuySettlement();

  const marketplaceAddr = config?.marketplace_address ?? null;
  const connected = (address ?? "").toLowerCase();
  const buyerConnected = !!session.wallet && connected === session.wallet;

  // Step 1+2: reserve cells + obtain the authority-signed permit (backend).
  const authorize = async () => {
    setError(null);
    try {
      setPhase("reserving");
      await marketplaceApi.prepareBuy(listing.order_hash);
      setPhase("authorizing");
      const p = await marketplaceApi.requestPermit(listing.order_hash);
      setPermit(p);
      setPhase("ready");
    } catch (e) {
      setError(errText(t, e));
      setPhase("review");
    }
  };

  // Step 3: the CONNECTED BUYER WALLET approves USDC (if needed) then fills.
  const completePurchase = () => {
    if (!permit || !marketplaceAddr) return;
    void buy.settle({
      chainId: listing.chain_id,
      usdc: listing.currency.toLowerCase() as `0x${string}`,
      marketplace: marketplaceAddr.toLowerCase() as `0x${string}`,
      buyer: connected as `0x${string}`,
      price: BigInt(listing.price_micro),
      order: buildFillOrder(listing),
      sellerSig: permit.seller_signature as `0x${string}`,
      permit: buildFillPermit(permit.permit),
      permitSig: permit.signature as `0x${string}`,
      maxPrice: BigInt(listing.price_micro),
    });
  };

  const bs = buy.state;
  const completed = bs.kind === "completed";

  // On a confirmed on-chain fill: refresh listing, browse and my-marketplace.
  useEffect(() => {
    if (bs.kind === "completed") {
      qc.invalidateQueries({ queryKey: ["mkt", "listing", listing.order_hash] });
      qc.invalidateQueries({ queryKey: ["mkt", "listings"] });
      qc.invalidateQueries({ queryKey: ["mkt", "mine"] });
    }
  }, [bs.kind, qc, listing.order_hash]);

  const authorizing = phase === "reserving" || phase === "authorizing";

  const completeLabel = (): string => {
    switch (bs.kind) {
      case "checking_allowance": return t("marketplace.detail.checkingAllowance");
      case "awaiting_approve_wallet":
      case "awaiting_fill_wallet": return t("marketplace.detail.confirmInWallet");
      case "approving": return t("marketplace.detail.approvingUsdc");
      case "filling": return t("marketplace.detail.settlingOnchain");
      default: return t("marketplace.detail.completePurchase");
    }
  };

  const settleError = ((): { title: string; hint: string } | null => {
    switch (bs.kind) {
      case "approve_rejected": return { title: t("marketplace.detail.buyFailed"), hint: t("marketplace.detail.approveRejected") };
      case "approve_reverted": return { title: t("marketplace.detail.buyFailed"), hint: t("marketplace.detail.approveReverted") };
      case "fill_rejected": return { title: t("marketplace.detail.buyFailed"), hint: t("marketplace.detail.fillRejected") };
      case "fill_reverted": return { title: t("marketplace.detail.buyFailed"), hint: t("marketplace.detail.fillReverted") };
      case "error": return { title: t("marketplace.detail.buyFailed"), hint: bs.error };
      default: return null;
    }
  })();

  let footer: React.ReactNode;
  if (completed) {
    footer = <MktButton variant="primary" className="w-full" testId="buy-modal-done" onClick={onClose}>{t("marketplace.detail.done")}</MktButton>;
  } else if (phase === "ready") {
    footer = (
      <div className="flex gap-3">
        <MktButton variant="ghost" className="flex-1" testId="buy-modal-cancel" onClick={onClose} disabled={buy.isPending}>{t("marketplace.detail.cancel")}</MktButton>
        <MktButton variant="primary" className="flex-1" testId="buy-modal-complete" onClick={completePurchase}
          disabled={buy.isPending || !buyerConnected || !marketplaceAddr}>
          {completeLabel()}
        </MktButton>
      </div>
    );
  } else {
    footer = (
      <div className="flex gap-3">
        <MktButton variant="ghost" className="flex-1" testId="buy-modal-cancel" onClick={onClose} disabled={authorizing}>{t("marketplace.detail.cancel")}</MktButton>
        <MktButton variant="primary" className="flex-1" testId="buy-modal-confirm" onClick={authorize}
          disabled={authorizing || !session.isAuthenticated || !config?.signer_available}>
          {phase === "reserving" ? t("marketplace.detail.reserving") : phase === "authorizing" ? t("marketplace.detail.authorizing") : t("marketplace.detail.reserveAuthorize")}
        </MktButton>
      </div>
    );
  }

  return (
    <Modal open onClose={onClose} title={t("marketplace.detail.buyTitle", { id: displayParcelId(listing) })} subtitle={t("marketplace.detail.buySubtitle")} testId="buy-modal" footer={footer}>
      {!session.isAuthenticated && (
        <div data-testid="buy-modal-signin" className="mb-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <p className="font-mono text-[10.5px] text-state-warning uppercase tracking-[0.14em]">{t("marketplace.detail.signInRequired")}</p>
          <a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.detail.connectSignIn")}</a>
        </div>
      )}
      {config && !config.signer_available && (
        <div className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2.5">
          <p className="font-mono text-[10.5px] text-state-danger">{t("marketplace.detail.settlementUnavailable")}</p>
        </div>
      )}
      <div className="space-y-3">
        <Row label={t("marketplace.detail.price")}><Money amount={fmtMicro(listing.price_micro)} symbol={sym} className="text-lg text-neon-teal" /></Row>
        <Row label={t("marketplace.detail.floor")}>{listing.floor_micro ? `${fmtMicro(listing.floor_micro)} ${sym}` : "—"}</Row>
        <Row label={t("marketplace.detail.gridFee")}>{config ? `${(config.grid_fee_bps / 100).toFixed(2)}%` : "—"}</Row>
      </div>

      {/* Stage 2 — reservation + authority permit signed (awaiting wallet settlement). */}
      {phase === "ready" && !completed && permit && (
        <div data-testid="buy-modal-ready" className="mt-5 rounded-lg border border-neon-teal/40 bg-neon-teal/10 p-4">
          <p className="font-display text-sm text-neon-teal uppercase tracking-[0.06em]">{t("marketplace.detail.settlementAuthorized")}</p>
          <p className="mt-2 font-mono text-[10.5px] text-text-secondary leading-relaxed">
            {t("marketplace.detail.settlementAuthorizedBody")}
          </p>
          <p className="mt-2 font-mono text-[10px] text-text-muted">{t("marketplace.detail.authorityFloor", { addr: shortAddr(permit.signer), amt: fmtMicro(permit.floor_price_micro), sym })}</p>
        </div>
      )}

      {/* The connected wallet must be the authenticated buyer to sign approve/fill. */}
      {phase === "ready" && !completed && session.isAuthenticated && !buyerConnected && (
        <div data-testid="buy-modal-wrong-wallet" className="mt-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <p className="font-mono text-[10.5px] text-state-warning leading-relaxed">{t("marketplace.detail.wrongBuyerWallet", { addr: shortAddr(session.wallet ?? "") })}</p>
        </div>
      )}

      {/* Stage 4 — confirmed on-chain purchase. */}
      {completed && (
        <div data-testid="buy-modal-complete-panel" className="mt-5 rounded-lg border border-state-success/40 bg-state-success/10 p-4">
          <p className="font-display text-sm text-state-success uppercase tracking-[0.06em]">{t("marketplace.detail.purchaseComplete")}</p>
          <p className="mt-2 font-mono text-[10.5px] text-text-secondary leading-relaxed">{t("marketplace.detail.purchaseCompleteBody")}</p>
        </div>
      )}

      {settleError && (
        <div data-testid="buy-modal-settle-error" className="mt-5 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{settleError.title}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{settleError.hint}</p>
        </div>
      )}

      {error && (
        <div data-testid="buy-modal-error" className="mt-5 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{error.title}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{error.hint}</p>
        </div>
      )}
    </Modal>
  );
}

function OfferModal({
  assetKey, config, session, onClose, onDone,
}: {
  assetKey: string;
  config?: MarketplaceConfig | null;
  session: ReturnType<typeof useSession>;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const currencies = (config?.currencies ?? []).filter((c) => c.enabled && c.address);
  const [currency, setCurrency] = useState(currencies[0]?.address ?? "");
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState(OFFER_DURATIONS[1].seconds);
  const [error, setError] = useState<{ title: string; hint: string } | null>(null);
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: async () => {
      const micro = Math.round(Number(amount) * 1_000_000);
      const expiry_market_ts = Math.floor(Date.now() / 1000) + duration;
      return marketplaceApi.createOffer({
        asset_key: assetKey,
        currency: currency.toLowerCase(),
        amount_micro: micro,
        expiry_market_ts,
        nonce: Date.now(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mkt", "offers", assetKey] });
      onDone();
      onClose();
    },
    onError: (e) => setError(errText(t, e)),
  });

  const micro = Math.round(Number(amount) * 1_000_000);
  const canSubmit = session.isAuthenticated && micro > 0 && !!currency && !mut.isPending;

  return (
    <Modal open onClose={onClose} title={t("marketplace.detail.makeOfferTitle")} subtitle={t("marketplace.detail.makeOfferSubtitle")} testId="offer-modal"
      footer={
        <div className="flex gap-3">
          <MktButton variant="ghost" className="flex-1" testId="offer-modal-cancel" onClick={onClose} disabled={mut.isPending}>{t("marketplace.detail.cancel")}</MktButton>
          <MktButton variant="primary" className="flex-1" testId="offer-modal-submit" onClick={() => { setError(null); mut.mutate(); }} disabled={!canSubmit}>
            {mut.isPending ? t("marketplace.detail.submitting") : t("marketplace.detail.submitOffer")}
          </MktButton>
        </div>
      }
    >
      {!session.isAuthenticated && (
        <div data-testid="offer-modal-signin" className="mb-4 rounded-md border border-state-warning/40 bg-state-warning/10 px-3 py-2.5">
          <a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.detail.connectSignIn")}</a>
        </div>
      )}
      <div className="space-y-4">
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.detail.amount")}</span>
          <div className="flex items-center gap-2">
            <input
              data-testid="offer-amount-input"
              type="number" inputMode="decimal" min="0" placeholder="0.00"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full h-10 px-3 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-display text-base focus:outline-none focus:border-neon-teal/60"
            />
            <select
              data-testid="offer-currency-select"
              value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="h-10 px-2 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none"
            >
              {currencies.map((c) => <option key={c.symbol} value={c.address ?? ""}>{c.symbol}</option>)}
            </select>
          </div>
        </label>
        <label className="block">
          <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block">{t("marketplace.detail.expiresIn")}</span>
          <select
            data-testid="offer-duration-select"
            value={duration} onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none"
          >
            {OFFER_DURATIONS.map((d) => <option key={d.seconds} value={d.seconds}>{t(d.labelKey)}</option>)}
          </select>
        </label>
      </div>
      {error && (
        <div data-testid="offer-modal-error" className="mt-5 rounded-lg border border-state-danger/40 bg-state-danger/10 p-4">
          <p className="font-display text-sm text-state-danger">{error.title}</p>
          <p className="mt-1.5 font-mono text-[10.5px] text-text-muted leading-relaxed">{error.hint}</p>
        </div>
      )}
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">{label}</span>
      <span className="font-display text-sm text-text-primary tabular-nums">{children}</span>
    </div>
  );
}
